import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import PageSpinner from "../../../components/PageSpinner/PageSpinner";
import { InlineSpinner } from "../../../components/Spinners/Spinners";
import { ToastType } from "../../../../types/ToastMessages";
import { setToastMessages } from "../../../redux/ToastMessages/ToastMessagesSlice";
import PractitionerProfileDetails from "../../../components/PractionerProfile/PractitionerProfileDetails";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { PractitionerProfile } from "../../../../types/PractitionerProfile";
import { appInsights } from "../../../../AppInsights";
import "./PractitionerProfileForm.css";
import { Organization } from "../../../../types/Organization";
import { updatePractitionerProfile } from "../../../../api/PractitionerProfileApi";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { clearPractitionerProfileData } from "../../../redux/PractitionerProfile/PractitionerProfileSlice";
import { getOrganizationList } from "../../../../api/OrganizationApi";

interface Props {}

function EditPractitionerProfileForm(props: Props) {
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validated, setValidated] = useState(false);
  const [organizationList, setOrganizationList] = useState<Organization[]>([]);

  const history = useHistory();
  const dispatch = useAppDispatch();
  const reduxToastMessages = useAppSelector((state) => state.toastMessages);

  const profileData: PractitionerProfile = useAppSelector(
    (state) => state.practitionerProfile
  );

  useEffect(() => {
    const loadOrganizations = async () => {

      const response = await getOrganizationList();
  
      const organizations = response?.data ? response.data : [];
      setOrganizationList(organizations);
  
      setIsPageLoading(false);
    };

    loadOrganizations();
  }, []);


  async function handleOnSubmit(event: any) {
    event.preventDefault();
    setIsSubmitting(true);
    const form = event.currentTarget;
    const isValid: boolean = form.checkValidity();

    setValidated(true);

    if (isValid) {
      let info: any[] = [];
      let errors: any[] = [];

      await updatePractitionerProfile(profileData).then((apiResponse) => {
        if (apiResponse?.data) {
          info.push("Practitioner profile updated successfully");

          if (appInsights !== undefined) {
            appInsights.trackEvent({
              name: "Practitioner Profile Updated",
            });
          }
        } else {
          errors.push(
            "An error occurred updating the practitioner profile. Please try again."
          );

          if (appInsights !== undefined) {
            appInsights.trackTrace({
              message: "Failed to update practitioner profile",
              severityLevel: SeverityLevel.Error,
            });
          }
        }
      });

      if (info.length > 0) {
        dispatch(
          setToastMessages({
            toastMessages: [
              ...reduxToastMessages.toastMessages,
              {
                toastType: ToastType.Info,
                message: info.toString(),
                title: "Practitioner Profile Form",
              },
            ],
          })
        );
      }

      if (errors.length > 0) {
        dispatch(
          setToastMessages({
            toastMessages: [
              ...reduxToastMessages.toastMessages,
              {
                toastType: ToastType.Error,
                message: errors.toString(),
                title: "Practitioner Profile Form",
              },
            ],
          })
        );
      }

      setValidated(false);
    } else {
      setFocusToFirstInvalidElement();
    }
    setIsSubmitting(false);
  }

  function returnToPractitionerProfileList() {
    dispatch(clearPractitionerProfileData());
    history.push("/Employee/Practitioners/ProfileList");
  }

  function setFocusToFirstInvalidElement() {
    let errorElements = document.querySelectorAll(
      ".form-control:invalid, .form-check-input:invalid"
    );
    if (errorElements !== undefined && errorElements.length > 0) {
      (errorElements[0] as HTMLInputElement).focus();
    }
  }

  return isPageLoading ? (
    <PageSpinner />
  ) : (
    <Container className="practitionerProfileContainer">
      <Form noValidate validated={validated} onSubmit={handleOnSubmit}>
        <PractitionerProfileDetails organizationList={organizationList} validated={validated} />
        <Row>
          <Form.Group id="formEntrySubmit">
            <Col className="flexRight">
              <Button
                id="formCancelButton"
                data-testid="formCancelButton"
                variant="outline-secondary"
                onClick={() => returnToPractitionerProfileList()}
                style={{ marginLeft: "0.5rem" }}
              >
                Cancel
              </Button>
              <Button
                id="formBackButton"
                data-testid="formBackButton"
                variant="outline-secondary"
                onClick={() => returnToPractitionerProfileList()}
                style={{ marginLeft: "0.5rem" }}
              >
                Back
              </Button>
              <Button
                id="formEntrySubmitButton"
                data-testid="formEntrySubmitButton"
                disabled={isSubmitting}
                variant="primary"
                type="submit"
                className=" align-items-center"
              >
                {isSubmitting && (
                  <InlineSpinner
                    for="button"
                    id="submitSpinner"
                    data-testid="submitSpinner"
                  />
                )}
                {!isSubmitting && "Update Profile"}
              </Button>
            </Col>
          </Form.Group>
        </Row>
      </Form>
    </Container>
  );
}

export default EditPractitionerProfileForm;
